import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  generateHeal,
  listCrawls,
  type CrawlSummary,
} from "../lib/api";

/**
 * "Compare with previous crawl" modal — opens from the Crawl Detail
 * action row. Plan §16 / Phase 3A v2: brings the heal-generation flow
 * into the UI without requiring the CLI.
 *
 * UX notes (matches the recommendation memo):
 *   - The CURRENT crawl is fixed (you clicked the button on its page),
 *     so we only ask the user to pick a PREVIOUS crawl.
 *   - We auto-default "previous" to the newest crawl-on-the-same-host
 *     that's NOT the current one, so the common case is one click.
 *   - We filter the dropdown to same-host crawls only — diffing across
 *     hosts produces 100% "missing" proposals which is noise.
 *   - Type-to-confirm-style friction is overkill here; the action is
 *     producing a NEW row, not destroying anything. Single CTA is
 *     fine, with a clear cancel.
 */
export function HealCompareDialog(props: {
  currentOutDir: string;
  currentTargetUrl: string;
  onClose: () => void;
}): JSX.Element {
  const { currentOutDir, currentTargetUrl, onClose } = props;
  const navigate = useNavigate();

  const [allCrawls, setAllCrawls] = useState<CrawlSummary[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [previousOutDir, setPreviousOutDir] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Fetch the user's crawls once on mount. listCrawls is already paged
  // at 50 by default — that's fine for the v1 picker; if a customer
  // accumulates >50 crawls on one host we'll add a search box.
  useEffect(() => {
    let alive = true;
    listCrawls()
      .then((rows) => {
        if (alive) setAllCrawls(rows);
      })
      .catch((err) => {
        if (alive) setLoadErr(err instanceof Error ? err.message : String(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  const currentHost = useMemo(() => hostOf(currentTargetUrl), [currentTargetUrl]);

  // Same-host crawls that are NOT the current one. Sorted newest-first
  // by crawledAt so the auto-default lands on "the immediately
  // preceding crawl" — the case the user almost always wants.
  const candidates = useMemo<CrawlSummary[]>(() => {
    if (!allCrawls) return [];
    return allCrawls
      .filter((c) => c.id !== currentOutDir && hostOf(c.targetUrl) === currentHost)
      .sort((a, b) => (b.crawledAt ?? "").localeCompare(a.crawledAt ?? ""));
  }, [allCrawls, currentOutDir, currentHost]);

  // Auto-select the newest candidate as soon as data arrives.
  useEffect(() => {
    if (!previousOutDir && candidates.length > 0) {
      setPreviousOutDir(candidates[0]!.id);
    }
  }, [candidates, previousOutDir]);

  const canSubmit = !!previousOutDir && !busy;

  async function handleSubmit(): Promise<void> {
    if (!previousOutDir) return;
    setBusy(true);
    setSubmitErr(null);
    try {
      const heal = await generateHeal({
        previousOutDir,
        currentOutDir,
      });
      // Land the user straight on the review page — they'll want to
      // see what the diff produced, then optionally Send-as-PR /
      // Send-as-Linear / Send-as-Jira from there.
      navigate(`/heals/${encodeURIComponent(heal.id)}`);
    } catch (err) {
      setSubmitErr(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="heal-compare-title"
    >
      <div className="w-full max-w-lg rounded-lg border border-slate-700 bg-slate-950 p-5 shadow-2xl">
        <h2
          id="heal-compare-title"
          className="text-base font-semibold text-slate-100"
        >
          Compare with previous crawl
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          UAIP will diff the two crawls and propose selector fixes for any
          drift. The result lands on the heal review page where you can
          accept, file as a Jira/Linear issue, or send as a PR.
        </p>

        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Current
            </div>
            <div className="mt-0.5 font-mono text-slate-200 truncate">
              {currentOutDir}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {currentTargetUrl}
            </div>
          </div>

          <div>
            <label
              htmlFor="heal-previous-select"
              className="text-xs uppercase tracking-wide text-slate-400"
            >
              Previous
            </label>
            {allCrawls === null && loadErr === null && (
              <div className="mt-1 text-xs text-slate-500">Loading…</div>
            )}
            {loadErr !== null && (
              <div className="mt-1 text-xs text-rose-400">
                Couldn&apos;t load your crawls: {loadErr}
              </div>
            )}
            {allCrawls !== null && candidates.length === 0 && (
              <div className="mt-1 rounded-md border border-amber-800/60 bg-amber-950/40 p-2 text-xs text-amber-200">
                No earlier crawl on{" "}
                <code className="font-mono text-amber-100">
                  {currentHost ?? "this host"}
                </code>{" "}
                yet. Re-crawl this site once to enable comparing.
              </div>
            )}
            {candidates.length > 0 && (
              <select
                id="heal-previous-select"
                value={previousOutDir}
                onChange={(e) => setPreviousOutDir(e.target.value)}
                disabled={busy}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 font-mono text-sm text-slate-100 focus:border-indigo-500 focus:outline-none"
              >
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id} — {fmtDate(c.crawledAt)} ({c.pageCount} pages)
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {submitErr !== null && (
          <p className="mt-3 text-sm text-rose-300">{submitErr}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:bg-indigo-900 disabled:text-indigo-400 disabled:cursor-not-allowed"
          >
            {busy ? "Generating…" : "Generate heal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Compact YYYY-MM-DD HH:MM in user's local timezone.
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
